import React, { useEffect, useMemo, useState, useRef, KeyboardEvent, ChangeEvent } from "react";
import { Link, useLocation } from "react-router-dom";
import { useReactTable, getCoreRowModel, getFilteredRowModel, getPaginationRowModel, createColumnHelper, ColumnDef } from "@tanstack/react-table";
import TablePaginationControls from "components/TableandmobilePaginationcontrol"
import { DesktopTableParts, MobileCardsOnly } from "components/TableandMobilecomponent";
import {
  DesktopTableLoader,
  MobileCardsLoader,
  DataTableStatus,
} from "components/DataTableLoader";
import { showSuccess, showError } from "toast/toast";
import { DynamicCost } from "types";
import DynamicCostHelpModal from "components/SignagedynamicCostToolTip";
import { EditIconButton, DeleteIconButton } from "components/DatatablesActionIcons";
import DeleteConfirmModal from "components/ToolTips/DeleteConfirmModal";
import { getAuthCookie } from "../../utils/authCookies";
import { useModuleActionPermissions } from "hooks/useModuleActionPermissions";
import { MODULE_PERMISSION_NAMES } from "utils/moduleActionPermissions";
const SignageDynamicCosts: React.FC = () => {
  const [rows, setRows] = React.useState<DynamicCost[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string>("");
  const [globalFilter, setGlobalFilter] = React.useState("");
  const [searchInput, setSearchInput] = useState<string>(globalFilter ?? "");
   const [openHelp, setOpenHelp] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; name: string } | null>(null);
  const [deleting, setDeleting] = useState<string | null>(null);
  const token = getAuthCookie("sessionToken");
  const location = useLocation();
  const isSignageTeammember = location.pathname.includes('/signageteammember');
  const baseRoute = isSignageTeammember ? '/signageteammember' : '/signage';

  const { canCreate, canView, canEdit, canDelete } = useModuleActionPermissions(
    MODULE_PERMISSION_NAMES.dynamic_costs
  );

  // fetch data
  React.useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_API_URL}/dynamic-costs`, {
          headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        });
        if (!res.ok) throw new Error("Failed to load dynamic costs");
        const data: DynamicCost[] = await res.json();
        if (alive) setRows(data ?? []);
      } catch (err) {
        setError(err instanceof Error ? err.message : "Unknown error");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [token]);

  const handleDeleteClick = (cost: DynamicCost) => {
    setDeleteTarget({ id: cost.dynamic_cost_id, name: cost.name || "Dynamic Cost" });
  };

  const handleDeleteCancel = () => {
    setDeleteTarget(null);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    await handleDelete(deleteTarget.id);
    setDeleteTarget(null);
  };

  const handleDelete = async (id: string) => {
    setDeleting(id);
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/dynamic-costs/${id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
      });
      if (!res.ok) throw new Error("Failed to delete the attribute.");

      setRows(prev => prev.filter(d => d.dynamic_cost_id !== id));
      showSuccess("Attribute deleted successfully!");
    } catch (err) {
      showError("Failed to delete the attribute.");
      setError(err instanceof Error ? err.message : "Unknown error while deleting.");
    } finally {
      setDeleting(null);
    }
  };

  // columns
  const h = createColumnHelper<DynamicCost>();
  const columns = React.useMemo<ColumnDef<DynamicCost, any>[]>(() => [
    h.display({
      id: "serial",
      header: () => <div className="th-text">S.No.</div>,
      cell: info => {
        const { pageIndex, pageSize } = info.table.getState().pagination;
        const serial = pageIndex * pageSize + info.row.index + 1;
        return <div className="text-sm">{serial}</div>;
      }
    }),
    h.accessor(row => row.name ?? "-", {
      id: "name",
      header: () => <div className="th-text">Name</div>,
      cell: info => <div className="text-sm">{info.getValue()}</div>,
    }),
    h.accessor(row => row.warehouse?.label ?? "-", {
      id: "warehouse",
      header: () => <div className="th-text">Warehouse</div>,
      cell: info => <div className="text-sm">{info.getValue()}</div>,
    }),
    h.accessor(row => row.base_cost ?? "-", {
      id: "base_cost",
      header: () => <div className="th-text">Base Cost</div>,
      cell: info => <div className="text-sm">{info.getValue()}</div>,
    }),
    h.accessor(row => row.distance_covered ?? "-", {
      id: "distance_covered",
      header: () => <div className="th-text">Distance Covered</div>,
      cell: info => <div className="text-sm">{info.getValue()}</div>,
    }),
    h.accessor(row => row.additional_cost_per_mile ?? "-", {
      id: "additional_cost_per_mile",
      header: () => <div className="th-text">Additional Cost</div>,
      cell: info => <div className="text-sm">{info.getValue()}</div>,
    }),
    h.accessor(row => row.status, {
      id: "status",
      header: () => <div className="th-text ">Status</div>,
      cell: info => (
        <div className="text-sm ">
          <input
            type="radio"
            className={info.getValue() ? "status-radio status-radio-active" : "status-radio status-radio-inactive"}
            checked
            readOnly
          />
        </div>
      ),
      enableSorting: false,
      enableGlobalFilter: false,
    }),
    h.accessor(row => row.is_default && row.cost_type?.includes("installation"), {
      id: "default_installation",
      header: () => <div className="th-text">Default Installation</div>,
      cell: (info) => {
        const value = info.getValue();
        return (
          <div className="text-sm">
            {value === true ? (
              <input
                type="checkbox"
                checked
                disabled
                className="form-check-input bg-success border-success"
                style={{
                  width: "1.2rem",
                  height: "1.2rem",
                  cursor: "not-allowed",
                }}
                title="Yes"
              />
            ) : (
              "--"
            )}
          </div>
        );
      },
      enableSorting: false,
      enableGlobalFilter: false,
    }),
    h.accessor(row => row.is_default && row.cost_type?.includes("removal"), {
      id: "default_removal",
      header: () => <div className="th-text">Default Removal</div>,
      cell: (info) => {
        const value = info.getValue();
        return (
          <div className="text-sm">
            {value === true ? (
              <input
                type="checkbox"
                checked
                disabled
                className="form-check-input bg-success border-success"
                style={{
                  width: "1.2rem",
                  height: "1.2rem",
                  cursor: "not-allowed",
                }}
                title="Yes"
              />
            ) : (
              "--"
            )}
          </div>
        );
      },
      enableSorting: false,
      enableGlobalFilter: false,
    }),
    h.display({
      id: "actions",
      header: () => <div className="th-text ">Actions</div>,
      cell: ({ row }) => (
        <div className="d-flex align-items-center gap-2">
          {canEdit && (
          <EditIconButton
            to={`${baseRoute}/addDynamicCost/${row.original.dynamic_cost_id}`}
            state={{ type: "edit" as const }}
            className="icon-square"
            title="Edit"
          />
          )}
          {canDelete && (
          <DeleteIconButton
            className="icon-square danger"
            title="Delete"
            onClick={() => handleDeleteClick(row.original)}
            disabled={deleting === row.original.dynamic_cost_id}
          />
          )}
        </div>
      ),
      enableSorting: false,
      enableGlobalFilter: false,
    }),
  ], [baseRoute, deleting, canEdit, canDelete]);

  // table instance
  const table = useReactTable({
    data: rows,
    columns,
    state: { globalFilter },
    onGlobalFilterChange: setGlobalFilter,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    // simple global filter: name + warehouse label
    globalFilterFn: (row, _columnId, filterValue: string) => {
      const v = (filterValue ?? "").toLowerCase().trim();
      if (!v) return true;
      const name = (row.original.name ?? "").toLowerCase();
      const wh = (row.original.warehouse?.label ?? "").toLowerCase();
      return name.includes(v) || wh.includes(v);
    },
    initialState: {
      pagination: { pageIndex: 0, pageSize: 10 },
    },
  });

  return (
    <div className="content container">
      <div className="d-flex justify-content-between align-items-center border-bottom pb-3 mb-2 page-container">
        <h4 className="mb-0 fw-bold"></h4>
        <div className="d-flex gap-3 align-items-center">
          {canCreate && (
            <Link to={`${baseRoute}/addDynamicCost`} className="btn btn-primary  table-addbutton" state={{ type: "add" as const }}>
            + Add Dynamic Cost
          </Link>
          )}
        </div>
      </div>
            <div className="po-header mb-4 py-4  shadow-border-datatables">
        <div className="d-flex justify-content-between align-items-start align-items-md-center gap-2 flex-wrap">
          <div>
           
            <div className="d-flex align-items-center gap-2">
  <h3 className="fs-22 m-0 roboto-new" style={{ color: "#111827" }}>
   <h4 className="mb-1 po-title">Dynamic Costs</h4>
  </h3>

  {/* Help Icon */}
  <button
    onClick={() => setOpenHelp(true)}
    className="d-inline-flex align-items-center justify-content-center p-0 border-0 bg-transparent"
    style={{ cursor: "pointer" }}
  >
    <span
      className="d-inline-flex align-items-center justify-content-center"
      style={{
        width: 26,
        height: 26,
        borderRadius: "50%",
        background: "#eef4ff",
      }}
    >
      <i
        className="bi bi-question-circle-fill"
        style={{ fontSize: 18, color: "#155dfc" }}
      ></i>
    </span>
  </button>

  <DynamicCostHelpModal
    open={openHelp}
    onClose={() => setOpenHelp(false)}
  />
</div>

            {/* <div className="po-subtitle">Configure distance-based pricing rules</div> */}
          </div>

          <div className="d-flex align-items-center gap-2 flex-wrap">
            <div className="input-group po-search" style={{ width: "clamp(240px, 28vw, 360px)" }}>
              <span className="input-group-text"><i className="ri-search-line" /></span>
              <input
                type="text"
                className="form-control"
                placeholder="Search by name or warehouse…"
                value={searchInput}
                onChange={(e: ChangeEvent<HTMLInputElement>) => setSearchInput(e.target.value)}
                onKeyDown={(e: KeyboardEvent<HTMLInputElement>) => {
                  if (e.key === "Enter") {
                    setGlobalFilter(searchInput);
                  }
                }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Table / Mobile cards */}
      {error && <div className="alert alert-danger mb-3">{error}</div>}

      <div className="card overflow-hidden production-card box-shadow-datatables shadow-border-datatables">
          {/* Header like screenshot (left title, right count pill) */}
          <div className="align-items-center bg-white border-bottom  d-flex justify-content-between px-4 py-4">
            <h5 className="mb-0 "></h5>
            {/* <span className="badge rounded-pill bg-body-tertiary text-secondary-emphasis px-3 py-2">
            orders
          </span> */}
          </div>
          <hr className="mb-2" style={{ borderTop: "none" }} />
          {/* DESKTOP (≥ 1022px) */}
          <div className="rdt-desktop ">
            <div
              className="production-scroll"
              style={{ "--rdt-min": "1200px" } as React.CSSProperties}
            >
              <table className="table production-table align-middle mb-0 table-1">
                {loading ? (
                  <DesktopTableLoader table={table} message="Loading dynamic costs…" />
                ) : (
                  <DesktopTableParts table={table} />
                )}
              </table>
            </div>
          </div>

          {/* MOBILE (≤ 1021px) */}
          <div className="rdt-mobile">
            {loading ? (
              <MobileCardsLoader message="Loading dynamic costs…" />
            ) : (
              <MobileCardsOnly
              table={table}
              idColId="order_link"
              titleColId="product_name"
              actionColId="actions"
            />
            )}
          </div>

        </div>

      <DataTableStatus
        loading={loading}
        isEmpty={table.getRowModel().rows.length === 0}
      >
        <div className="mt-4 mb-4">
          <TablePaginationControls table={table} />
        </div>
      </DataTableStatus>
      <DeleteConfirmModal
        open={!!deleteTarget}
        dynamicName={deleteTarget?.name || "Dynamic Cost"}
        onCancel={handleDeleteCancel}
        onDelete={handleDeleteConfirm}
      />
    </div>
  );
};

export default SignageDynamicCosts;
